import React, { useState, useEffect } from 'react';
import { Upload, Power, Calendar, MessageSquare, Activity, CheckCircle, XCircle, Loader } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionSite = () => {
  const [config, setConfig] = useState({
    logo_url: '',
    ferme_temporairement: 'non',
    date_fermeture_debut: '',
    date_fermeture_fin: '',
    message_fermeture: '',
    watchdog_actif: 'non'
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [watchdogStatus, setWatchdogStatus] = useState(null);

  useEffect(() => {
    loadConfiguration();
    checkWatchdogStatus();
  }, []);

  const loadConfiguration = async () => {
    try {
      const response = await fetch(`${API_BASE}/configuration`);
      const data = await response.json();
      
      const configObj = {};
      data.forEach(item => {
        configObj[item.param_name] = item.param_value;
      });
      
      setConfig(configObj);
      setLogoPreview(configObj.logo_url);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement config:', error);
      setLoading(false);
    }
  };

  const checkWatchdogStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/watchdog/status`);
      const data = await response.json();
      setWatchdogStatus(data);
    } catch (error) {
      console.error('Erreur vérification watchdog:', error);
      setWatchdogStatus({ actif: false, erreur: true });
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('❌ Image trop grande ! Maximum 2MB');
        return;
      }
      
      // Vérifier le type
      if (!file.type.startsWith('image/')) {
        alert('❌ Fichier invalide ! Utilisez une image (PNG, JPG, etc.)');
        return;
      }
      
      setLogoFile(file);
      
      // Prévisualisation
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      alert('❌ Sélectionnez d\'abord une image');
      return;
    }
    
    setUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await fetch(`${API_BASE}/site/upload-logo`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConfig(prev => ({ ...prev, logo_url: data.url }));
        setLogoPreview(data.url);
        setLogoFile(null);
        alert('✅ Logo mis à jour avec succès !');
      } else {
        alert(`❌ Erreur : ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur upload logo:', error);
      alert('❌ Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    
    try {
      // Sauvegarder tous les paramètres
      for (const [key, value] of Object.entries(config)) {
        if (key !== 'logo_url') { // Logo géré séparément
          await fetch(`${API_BASE}/configuration/${key}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param_value: value })
          });
        }
      }
      
      alert('✅ Configuration sauvegardée !');
      
      // Si watchdog modifié, l'activer/désactiver
      if (config.watchdog_actif) {
        await toggleWatchdog(config.watchdog_actif === 'oui');
      }
      
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleWatchdog = async (activer) => {
    try {
      const response = await fetch(`${API_BASE}/watchdog/${activer ? 'start' : 'stop'}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        checkWatchdogStatus();
        alert(`✅ Watchdog ${activer ? 'activé' : 'désactivé'}`);
      } else {
        alert(`❌ Erreur : ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur watchdog:', error);
      alert('❌ Erreur lors du contrôle du watchdog');
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Gestion du Site</h2>
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 🖼️ SECTION LOGO */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Logo de la Ferme
          </h3>
          
          <div className="space-y-4">
            {/* Prévisualisation */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              {logoPreview ? (
                <div className="space-y-3">
                  <img 
                    src={logoPreview} 
                    alt="Logo preview" 
                    className="max-h-32 mx-auto object-contain"
                  />
                  <p className="text-sm text-gray-500">Prévisualisation</p>
                </div>
              ) : (
                <div className="py-8">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Aucun logo défini</p>
                </div>
              )}
            </div>
            
            {/* Upload */}
            <div>
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoFileChange}
                className="hidden"
              />
              <label
                htmlFor="logo-upload"
                className="block w-full text-center px-4 py-3 border-2 border-blue-300 rounded-md cursor-pointer hover:bg-blue-50 transition-colors"
              >
                📁 Choisir une image
              </label>
              <p className="text-xs text-gray-500 mt-2">
                PNG, JPG ou GIF • Maximum 2MB • Recommandé : 500x500px
              </p>
            </div>
            
            {logoFile && (
              <button
                onClick={handleUploadLogo}
                disabled={uploadingLogo}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {uploadingLogo ? (
                  <>
                    <Loader className="inline w-4 h-4 animate-spin mr-2" />
                    Upload en cours...
                  </>
                ) : (
                  '⬆️ Télécharger le logo'
                )}
              </button>
            )}
            
            {config.logo_url && (
              <div className="text-xs text-gray-500 break-all bg-gray-50 p-2 rounded">
                <strong>URL actuelle :</strong> {config.logo_url}
              </div>
            )}
          </div>
        </div>

        {/* 🔴 SECTION FERMETURE TEMPORAIRE */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Power className="w-5 h-5 text-red-600" />
            Fermeture Temporaire
          </h3>
          
          <div className="space-y-4">
            {/* Toggle fermeture */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">État du site</div>
                <div className="text-sm text-gray-600">
                  {config.ferme_temporairement === 'oui' ? 'Fermé aux réservations' : 'Ouvert'}
                </div>
              </div>
              <button
                onClick={() => updateConfig('ferme_temporairement', config.ferme_temporairement === 'oui' ? 'non' : 'oui')}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  config.ferme_temporairement === 'oui' ? 'bg-red-600' : 'bg-green-600'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    config.ferme_temporairement === 'oui' ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {/* Dates de fermeture */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Date début
                </label>
                <input
                  type="date"
                  value={config.date_fermeture_debut || ''}
                  onChange={(e) => updateConfig('date_fermeture_debut', e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Date fin
                </label>
                <input
                  type="date"
                  value={config.date_fermeture_fin || ''}
                  onChange={(e) => updateConfig('date_fermeture_fin', e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            
            {/* Message de fermeture */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                Message affiché
              </label>
              <textarea
                value={config.message_fermeture || ''}
                onChange={(e) => updateConfig('message_fermeture', e.target.value)}
                className="w-full p-3 border rounded-md h-32 focus:ring-red-500 focus:border-red-500"
                placeholder="Ex: Nous sommes en vacances du 20 au 25 octobre.&#10;&#10;Merci de votre compréhension !"
              />
            </div>
            
            {/* Avertissement */}
            {config.ferme_temporairement === 'oui' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ Le site est actuellement FERMÉ aux réservations !
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Les clients verront un message de fermeture au lieu du formulaire de connexion.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 📊 SECTION WATCHDOG */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />
            Watchdog (Surveillance Serveur)
          </h3>
          
          <div className="space-y-4">
            {/* Statut */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium text-gray-900">État du watchdog</div>
                {watchdogStatus?.actif ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-gray-400" />
                )}
              </div>
              
              {watchdogStatus?.erreur ? (
                <p className="text-sm text-red-600">❌ Erreur de connexion</p>
              ) : watchdogStatus?.actif ? (
                <div className="text-sm text-green-700 space-y-1">
                  <p>✅ Surveillance active</p>
                  {watchdogStatus.uptime && (
                    <p className="text-gray-600">⏱️ Uptime: {watchdogStatus.uptime}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">⏸️ Surveillance désactivée</p>
              )}
            </div>
            
            {/* Toggle watchdog */}
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div>
                <div className="font-medium text-gray-900">Activer la surveillance</div>
                <div className="text-sm text-gray-600">
                  Monitore l'état du serveur et redémarre si nécessaire
                </div>
              </div>
              <button
                onClick={() => updateConfig('watchdog_actif', config.watchdog_actif === 'oui' ? 'non' : 'oui')}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  config.watchdog_actif === 'oui' ? 'bg-purple-600' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    config.watchdog_actif === 'oui' ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {/* Info watchdog */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ À quoi sert le watchdog ?</strong>
              </p>
              <ul className="text-xs text-blue-700 mt-2 space-y-1">
                <li>• Surveille l'état de santé du serveur</li>
                <li>• Redémarre automatiquement en cas de crash</li>
                <li>• Envoie des alertes en cas de problème</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 💡 AIDE */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">💡 Aide</h3>
          
          <div className="space-y-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900 mb-1">✅ Bonnes pratiques</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Testez la fermeture en mode test avant de l'activer</li>
                <li>• Préparez votre message de fermeture à l'avance</li>
                <li>• Activez le watchdog pour plus de stabilité</li>
              </ul>
            </div>
            
            <div className="p-3 bg-yellow-50 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-1">⚠️ Attention</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• N'oubliez pas de sauvegarder après chaque modification</li>
                <li>• La fermeture bloque TOUTES les réservations</li>
                <li>• L'admin reste toujours accessible</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestionSite;